import { z } from 'zod';
/**
 * The machine-readable AI mark the EU AI-Act (Article 50(2)) requires on
 * AI-generated content, shared by every API response and persisted artifact
 * that carries such content (plan 022 §4).
 *
 * The object is optional everywhere it appears: its PRESENCE is the mark, so
 * deterministic and legacy content simply omits it — `aiGenerated` is a literal
 * `true` rather than a boolean because a `false` value would be meaningless.
 *
 * Every descriptive field is optional because the generation source does not
 * always know it: fallback prompts carry no Langfuse name or version, and an
 * artifact merged from several model calls records only the foundation model.
 */ export const AiProvenanceSchema = z.object({
    aiGenerated: z.literal(true),
    /** Resolved AiModel name, e.g. "gpt-5.4". Absent only on legacy content. */ foundationModel: z.string().optional(),
    /** Langfuse prompt name. Absent on fallback prompts and multi-prompt artifacts. */ promptName: z.string().optional(),
    /** Langfuse prompt version. Absent on fallback prompts and multi-prompt artifacts. */ promptVersion: z.number().int().optional(),
    generatedAt: z.iso.datetime().optional()
});
/**
 * Build the mark for one piece of generated content. Shared by the backend
 * services and the app-sdk so the construction rules live in one place.
 *
 * `foundationModel` must be the RESOLVED model name (the name the resolver
 * returned), never the requested setting — an unknown or stale setting silently
 * runs on the default model, and a mark naming the wrong model is a false claim.
 *
 * Absent fields — including `foundationModel`, which an older app host may not
 * report back over the AI RPC — are omitted rather than assigned `undefined`: a
 * Mongoose `$set`/`$push` of an explicit `undefined` persists `null`, which the
 * optional (non-nullable) contract field then rejects when the document is read
 * back, voiding the whole mark.
 */ export function buildAiProvenance(foundationModel, prompt) {
    return {
        aiGenerated: true,
        ...foundationModel !== undefined ? {
            foundationModel
        } : {},
        ...prompt?.promptName !== undefined ? {
            promptName: prompt.promptName
        } : {},
        ...prompt?.promptVersion !== undefined ? {
            promptVersion: prompt.promptVersion
        } : {},
        generatedAt: new Date().toISOString()
    };
}

//# sourceMappingURL=ai-provenance.js.map